import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useFormState } from 'react-hook-form'
import { Link, useLocation, useParams } from 'react-router-dom'
import { organizationConnectionUrl } from '../../../organization/organization-breadcrumb'
import { BreadcrumbView } from '../../../../../../context/breadcrumb.context'
import { useParamsInt } from '../../../../../../shared/hooks/location'
import { IconEdit, IconOrganization, IconPolicyFinding, IconRole } from '../../../../../../shared/icon/icon'
import { useApiFormProvider, useApiFormProviderDiscover, useApiFormProviderClaimMapping, useApiFormProviderIcon } from '../../../../../../api-new/state/auth'
import { useRoleSysProvider } from '../../../../../../role/hook/role-sys'
import { Menu, MenuDelete, MenuItem } from '../../../../../../shared/menu/menu'
import { FormNew } from '../../../../../../shared/form-new/form'
import { WorkspaceShell, WorkspaceSection } from '../../../../../../shared/sso-workspace/workspace-shell'
import { connectionUse } from '../../../../../../shared/sso-workspace/connection-use'
import { connectionTiles } from '../../../../../../shared/sso-workspace/status-strip'
import { SaveBar } from '../../../../../../shared/sso-workspace/save-bar'
import { LeaveGuard } from '../../../../../../shared/sso-workspace/leave-guard'
import { UnsavedChangesProvider } from '../../../../../../shared/sso-workspace/unsaved-changes'
import { claimUsage, claimChipModel } from '../../../../../../shared/sso-workspace/claims'
import { scrollToSection } from '../../../../../../shared/sso-workspace/section-nav'
import { ProviderLogo } from '../../../../../../shared/sso-workspace/provider-logo'
import { Notice } from '../../../../../../shared/sso-workspace/fleet-page'
import { useConnectionData } from './use-connection-data'
import { SectionIdentity } from './section-identity'
import { SectionCredentials } from './section-credentials'
import { SectionRouting } from './section-routing'
import { SectionClaims } from './section-claims'
import { SectionSignout } from './section-signout'
import { SectionTesting } from './section-testing'
import { SectionHelp } from './section-help'
import { SectionValues } from './section-values'
import { SectionActivity } from './section-activity'
import { SectionProvisioning } from './section-provisioning'
import { useScimLinks } from '../../../../../../shared/scim/use-scim-links'
import { baseURLOf, SCIM_TITLE } from '../../../../../../shared/scim/scim-words'
import { toProviderFormState, fromProviderFormState, invalidFieldsMessage, connectionSubtitle } from './fields'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../../../shared/audit/history/audit-record-context'

export const WORKSPACE_SECTIONS = [
  { id: 'idp', label: 'Identity provider' },
  { id: 'creds', label: 'Credentials', staff: true },
  { id: 'routing', label: 'Domains & organizations' },
  { id: 'claims', label: 'Claims & mapping' },
  { id: 'provisioning', label: SCIM_TITLE },
  { id: 'activity', label: 'Sign-in activity' },
  { id: 'signout', label: 'Sign-out' },
  // Staff-only, except on a connection an organization runs itself (its own,
  // on the shared app): its admins edit the test addresses too, on its
  // connection page, so there the section carries no Staff tag (see below).
  { id: 'testing', label: 'Testing', staff: true },
  { id: 'help', label: 'Contacts & help' },
  { id: 'values', label: 'Values for your identity provider' }
]

const SAVED_MS = 2000
const HIGHLIGHT_MS = 1800

export const DirtyGuard = () => {
  const { isDirty } = useFormState()
  return <LeaveGuard when={isDirty} />
}

// A save error belongs to the edit it was about: once nothing is unsaved
// (the edit was discarded, undone by hand, or saved), it goes, so the save
// bar can hide instead of sticking on an error for changes that are gone.
export const ClearErrorWhenClean = ({ error, onClear }) => {
  const { isDirty } = useFormState()
  useEffect(() => { if (!isDirty && error) onClear() }, [isDirty, error]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

// The header's Save mirrors the save bar: disabled until something changes.
export const HeaderSave = () => {
  const { isDirty, isSubmitting } = useFormState()
  return <button type="submit" className="btn sw-btn-primary" disabled={!isDirty || isSubmitting}>Save</button>
}

// The connection workspace (spec 2026-09-24) for one provider row: status
// strip, section nav and the sections, all inside one FormNew whose Save
// sends the existing PUT /provider/:id body.
export const ProviderWorkspace = () => {
  const { providerID } = useParamsInt()
  // Opened from an organization's SSO tab (its address names the
  // organization), Delete stays under that organization and returns to its tab.
  const { organizationID } = useParams()
  const connectionUrl = organizationID ? organizationConnectionUrl(organizationID, providerID) : `/admin/sso/connections/${providerID}`
  const apiFormProvider = useApiFormProvider()
  const discover = useApiFormProviderDiscover()
  const claimMappingApi = useApiFormProviderClaimMapping()
  const iconApi = useApiFormProviderIcon()
  const roleSysProvider = useRoleSysProvider()
  const data = useConnectionData(providerID)
  // Provisioning (SCIM): content's rows for every organization linked to
  // this connection; the Values section reads its base URL from them.
  const scim = useScimLinks({ providerID })
  const [discovery, setDiscovery] = useState(null)
  const [saveError, saveFailure, setSaveError] = useErrorLine()
  const [saved, setSaved] = useState(false)
  const [filled, setFilled] = useState({ keys: [], pulse: 0 })
  const [highlightRule, setHighlightRule] = useState(null)
  const savedTimer = useRef(null)
  const ruleTimer = useRef(null)
  const canEdit = Boolean(roleSysProvider.accessUpdate)
  // The organization runs this connection itself (auth's selfServe): it owns
  // it and it is on EducationXR's shared app. Its admins change its test
  // addresses too, so Testing isn't staff-only here.
  const selfServeOf = p => Boolean(p && p.organizationID && p.connection === 'shared_app')


  useEffect(() => () => {
    if (savedTimer.current) clearTimeout(savedTimer.current)
    if (ruleTimer.current) clearTimeout(ruleTimer.current)
  }, [])

  const provider = apiFormProvider.form
  // Whose connection this is (providers.organization_id), once loaded:
  // null when platform-held, undefined until this connection's row has
  // loaded. History reads it for a reader without SYS_AUDIT_LOG
  // (record-routes.js recordOrg); the domain picker reads it too, since only
  // a platform-held connection offers platform-held domains.
  const loaded = provider && provider.id === providerID
  const ownerOrganizationID = loaded ? (provider.organizationID ?? null) : undefined
  useAuditRecord({ type: 'providers', id: loaded ? providerID : undefined, children: true, organizationID: ownerOrganizationID })
  const formState = useMemo(() => toProviderFormState(provider), [provider])
  const selfServe = selfServeOf(provider)
  const { health, domains, links, mappings } = data
  const usedClaims = useMemo(() => Object.keys(claimUsage(mappings || [], links || [])), [mappings, links])
  const tiles = useMemo(() => connectionTiles({ provider, health, domains, links, mappings }), [provider, health, domains, links, mappings])

  // The edit route isn't exact, so this page stays mounted under its
  // sub-pages (organization, mapping, delete) and onPath runs again on the
  // way back: reload the lists as well as the row, since the sub-page may
  // have changed them. The first call is the mount, which useConnectionData
  // already loads for.
  const pathed = useRef(false)
  const handlePath = async () => {
    if (pathed.current) { data.reload.all(); scim.reload() }
    pathed.current = true
    await apiFormProvider.get(providerID)
  }
  const handleLookup = issuer => discover.get(encodeURIComponent(issuer))
  const handleLoadIcons = issuer => iconApi.get(issuer ? encodeURIComponent(issuer) : undefined)
  const loadedIcon = useMemo(() => ({ iconSource: provider.iconSource || 'auto', iconSVG: provider.iconSVG, iconSVGDark: provider.iconSVGDark }), [provider.iconSource, provider.iconSVG, provider.iconSVGDark])
  const handleSubmit = async form => {
    setSaveError(null)
    try {
      await apiFormProvider.put(form.id, fromProviderFormState(form, provider))
      data.reload.health()
      setSaved(true)
      if (savedTimer.current) clearTimeout(savedTimer.current)
      savedTimer.current = setTimeout(() => setSaved(false), SAVED_MS)
    } catch (exp) {
      setSaveError((exp && exp.data && exp.data.message) || 'Could not save.', exp)
    }
  }

  const handleInvalid = errors => {
    setSaveError(invalidFieldsMessage(errors))
  }

  // "Show rule" on a claim chip: jump to the routing section and flash the
  // organization whose rule reads that claim.
  const showRule = claim => {
    const link = (links || []).find(l => l.matchData && l.matchData.claim === claim)
    if (!link) return
    scrollToSection('routing')
    setHighlightRule(link.organizationName)
    if (ruleTimer.current) clearTimeout(ruleTimer.current)
    ruleTimer.current = setTimeout(() => setHighlightRule(null), HIGHLIGHT_MS)
  }

  // What the issuer publishes: this session's lookup first, else the last
  // health check's discovery.
  const published = key => (discovery && discovery[key]) || (health && health.discovery && health.discovery[key])
  // The claims the Claims & mapping chips show, offered by the rule editor.
  const claimsSupported = published('claimsSupported')
  const claimNames = useMemo(() => claimChipModel(claimsSupported || [], claimUsage(mappings || [], links || [])).map(c => c.claim),
    [claimsSupported, mappings, links])

  // A #section in the address (the old organization pages redirect to
  // #routing) scrolls there once the connection has loaded.
  const { hash } = useLocation()
  useEffect(() => { if (hash && provider.id) scrollToSection(hash.slice(1)) }, [hash, provider.id])

  // Organizations and role mappings live in this page's own sections now
  // (the old sub-pages redirect here), so the menu jumps to them (audit P2).
  const HeaderMenu = () =>
    <Menu>
      <MenuItem Icon={IconOrganization} text="domains & organizations" onClick={() => scrollToSection('routing')} show />
      <MenuItem Icon={IconRole} text="role mappings" onClick={() => scrollToSection('claims')} show />
      <MenuItem Icon={IconPolicyFinding} text="findings" to={`/admin/sso/findings?providerID=${providerID}`} show />
      <MenuDelete to={`${connectionUrl}/delete`} show={roleSysProvider.accessDelete} />
    </Menu>

  // A platform sign-in method's internal row (auth's `signinMethod`, design
  // .superpowers/signin-methods §1.2) is no connection: auth refuses its
  // edits here (409 provider.signin_method), so the page points to the
  // method's own screen instead of a form.
  if (provider.signinMethod) {
    return (
      <BreadcrumbView Icon={IconEdit} text={provider.providerName || 'Connection'} onPath={handlePath}>
        <div className="sw-pane p-3" data-testid="workspace-pane">
          <Notice>
            <p className="m-0" data-testid="signin-method-anchor">
              {`${provider.displayName || provider.providerName} is a sign-in method's own record, not a connection. `}
              <Link to={`/admin/signin-methods/${encodeURIComponent(provider.signinMethod)}`}>Open its sign-in method</Link> to change it.
            </p>
          </Notice>
        </div>
      </BreadcrumbView>
    )
  }

  return (
    <BreadcrumbView Icon={IconEdit} text={provider.providerName || 'Connection'} onPath={handlePath}>
      <div className="sw-pane p-3" data-testid="workspace-pane">
        <FormNew state={formState} enabled={roleSysProvider.accessUpdate} onSubmit={handleSubmit} onInvalid={handleInvalid}>
          <UnsavedChangesProvider>
          <WorkspaceShell
            title={provider.providerName}
            subtitle={connectionSubtitle(provider)}
            logo={<ProviderLogo iconSVG={provider.iconSVG} iconSVGDark={provider.iconSVGDark} />}
            status={provider.active === undefined ? undefined : connectionUse({ active: provider.active, isPublic: provider.public, bound: Array.isArray(domains) ? domains.length : undefined, testers: Array.isArray(provider.testAllowlist) ? provider.testAllowlist.length : undefined })}
            headerActions={
              <React.Fragment>
                <span role="status" className="small text-success align-self-center">{saved ? 'Saved' : ''}</span>
                {canEdit && <HeaderSave />}
                <HeaderMenu />
              </React.Fragment>
            }
            tiles={tiles}
            sections={WORKSPACE_SECTIONS}
            mode="staff"
            onGo={scrollToSection}>
            <WorkspaceSection id="idp" title="Identity provider" lede="Who people sign in with. Look up the issuer to check it and fill in what it publishes." mode="staff">
              <SectionIdentity mode="staff" onLookup={handleLookup} usedClaims={usedClaims} onJump={scrollToSection} onDiscovered={setDiscovery} onFilled={setFilled}
                tenantLocked={provider.connection === 'shared_app'}
                onLoadIcons={handleLoadIcons} loadedIcon={loadedIcon}
                noRoute={Array.isArray(domains) && domains.length === 0 && !provider.public
                  ? "No domain is bound and it isn't a public sign-in method, so nobody is sent to it yet."
                  : null}
                onFixRoute={() => scrollToSection('routing')}
                status={{
                  servingNow: Boolean(provider.active) && (Boolean(provider.public) || (Array.isArray(domains) && domains.length > 0)),
                  testers: Array.isArray(provider.testAllowlist) ? provider.testAllowlist.length : undefined,
                  connectionName: provider.displayName || provider.providerName,
                  domains: Array.isArray(domains) && domains.length ? domains.map(d => d.domain || d).join(', ') : undefined
                }} />
            </WorkspaceSection>
            <WorkspaceSection id="creds" title="Credentials" lede="How EducationXR proves who it is to the identity provider. Stored values are never shown again." staff mode="staff">
              <SectionCredentials discovery={discovery || (health && health.discovery)} health={health} showCheck />
            </WorkspaceSection>
            <WorkspaceSection id="routing" title="Domains & organizations" lede="Who is sent to this connection, and which organization they join." mode="staff">
              <SectionRouting mode="staff" providerID={providerID} domains={domains} links={links}
                claims={claimNames} onLinksChanged={() => Promise.all([data.reload.links(), scim.reload()])} highlightRule={highlightRule} canEdit={canEdit}
                canUnlink={Boolean(roleSysProvider.accessDelete)}
                connectionName={provider.displayName || provider.providerName} providerActive={Boolean(provider.active)} providerPublic={Boolean(provider.public)}
                ownerOrganizationID={ownerOrganizationID} onDomainsChanged={data.reload.domains}
                onProviderChanged={() => apiFormProvider.get(providerID)} />
            </WorkspaceSection>
            <WorkspaceSection id="claims" title="Claims & mapping" lede="What the identity provider tells us about each person, and what EducationXR does with it. Click a claim for details." mode="staff">
              <SectionClaims claimsSupported={published('claimsSupported') || []} mappings={mappings} links={links} providerID={providerID}
                canEdit={canEdit} onMappingsChanged={data.reload.mappings} onShowRule={showRule}
                onDeleteMapping={id => claimMappingApi.delete(id)} />
            </WorkspaceSection>
            <WorkspaceSection id="provisioning" title={SCIM_TITLE} lede="Let an organization's identity provider add, update and remove its people through SCIM 2.0. Each linked organization gets its own token, shown once." mode="staff">
              <SectionProvisioning providerID={providerID} links={scim.links} loadFailure={scim.failure} onLinksChanged={scim.reload}
                canEdit={canEdit} providerPublic={Boolean(provider.public)} />
            </WorkspaceSection>
            <WorkspaceSection id="activity" title="Sign-in activity" lede="Whether people sign in through this connection, and what the sign-in rules blocked or reported." mode="staff">
              <SectionActivity providerID={providerID} />
            </WorkspaceSection>
            <WorkspaceSection id="signout" title="Sign-out" lede="What happens when someone signs out of EducationXR." mode="staff">
              <SectionSignout fromIssuer={filled.keys.includes('signoutURL')} pulseKey={filled.pulse} issuerEndpoint={published('endSessionEndpoint') || ''} />
            </WorkspaceSection>
            <WorkspaceSection id="testing" title="Testing" staff={!selfServe} mode="staff" lede="While this list has addresses, only these people are sent to this connection. Everyone else keeps signing in the way they do now.">
              <SectionTesting ownerName={selfServe ? (provider.organizationName || 'The organization that owns it') : null} />
            </WorkspaceSection>
            <WorkspaceSection id="help" title="Contacts & help" lede="Who we warn about expiring credentials, and what people see if they aren't set up yet." mode="staff">
              <SectionHelp />
            </WorkspaceSection>
            <WorkspaceSection id="values" title="Values for your identity provider" lede="Paste these into the app registration. They never change." mode="staff">
              <SectionValues identifier={provider.identifier} id={provider.id} hasPrivateKey={provider.hasPrivateKey} scimBaseURL={baseURLOf(scim.links)} />
            </WorkspaceSection>
          </WorkspaceShell>
          {canEdit && <SaveBar error={saveError} failure={saveFailure} onDiscard={() => setSaveError(null)} />}
          </UnsavedChangesProvider>
          <DirtyGuard />
          <ClearErrorWhenClean error={saveError} onClear={() => setSaveError(null)} />
        </FormNew>
      </div>
    </BreadcrumbView>
  )
}
